import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  CheckCircle2, Circle, Loader2, Rocket, Copy, Check, ExternalLink, AlertCircle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function CopyButton({ text, label = "copy" }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      console.error("Clipboard copy failed", e);
    }
  };
  return (
    <button
      type="button"
      onClick={onCopy}
      data-testid={`copy-${label}`}
      className="inline-flex items-center gap-1 rounded-full bg-leaf-100 px-2.5 py-1 text-xs font-bold text-leaf-800 hover:bg-leaf-200"
    >
      {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

function ProgressBar({ done, total }) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  return (
    <div data-testid="deploy-progress">
      <div className="mb-2 flex items-baseline justify-between">
        <span className="text-sm font-bold text-leaf-900">
          {done} of {total} steps complete
        </span>
        <span className="font-display text-2xl font-black text-leaf-700">
          {pct}%
        </span>
      </div>
      <div className="h-3 w-full overflow-hidden rounded-full bg-leaf-50">
        <div
          className="h-full rounded-full bg-leaf-600 transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function StepItem({ step, idx, savingId, onToggle, onNote }) {
  return (
    <li
      data-testid={`deploy-step-${step.id}`}
      className={`relative rounded-3xl border-2 p-5 transition-all sm:p-6 ${
        step.done
          ? "border-leaf-300 bg-leaf-50"
          : "border-leaf-100 bg-white hover:border-leaf-300"
      }`}
    >
      <div className="flex items-start gap-4">
        <button
          type="button"
          onClick={() => onToggle(step.id, { done: !step.done })}
          aria-label={step.done ? "Mark as incomplete" : "Mark as complete"}
          data-testid={`deploy-step-toggle-${step.id}`}
          className="shrink-0"
        >
          {step.done ? (
            <CheckCircle2 className="h-7 w-7 text-leaf-600" />
          ) : (
            <Circle className="h-7 w-7 text-leaf-300" />
          )}
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="font-display text-2xl font-black text-leaf-300">
              {String(idx + 1).padStart(2, "0")}
            </span>
            <h3
              className={`font-display text-xl font-bold sm:text-2xl ${
                step.done ? "text-leaf-700 line-through" : "text-leaf-900"
              }`}
            >
              {step.title}
            </h3>
            {savingId === step.id && (
              <Loader2 className="h-4 w-4 animate-spin text-leaf-700" />
            )}
          </div>
          <p className="mt-2 text-leaf-900/80">{step.desc}</p>

          {step.tip && (
            <p className="mt-2 rounded-xl bg-sun-100 px-3 py-2 text-sm text-leaf-900">
              <span className="font-bold">Tip:</span> {step.tip}
            </p>
          )}

          <details className="group mt-3" open={!!step.note}>
            <summary
              data-testid={`deploy-step-note-toggle-${step.id}`}
              className="cursor-pointer select-none text-xs font-bold uppercase tracking-widest text-leaf-700 hover:text-leaf-900"
            >
              {step.note ? "Edit note" : "Add a note"}
            </summary>
            <textarea
              data-testid={`deploy-step-note-${step.id}`}
              rows={2}
              placeholder="Anything to remember? (e.g. chosen subdomain, CNAME target)"
              defaultValue={step.note}
              onBlur={(e) => {
                if (e.target.value !== step.note) {
                  onNote(step.id, { note: e.target.value });
                }
              }}
              className="mt-2 w-full rounded-xl border-2 border-stone-200 bg-stone-50 px-3 py-2 text-sm text-leaf-900 placeholder:text-stone-400 focus:border-leaf-500 focus:bg-white focus:outline-none focus:ring-4 focus:ring-leaf-200"
            />
          </details>
        </div>
      </div>
    </li>
  );
}

export default function DeployTab() {
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [savingId, setSavingId] = useState(null);
  const previewUrl = (typeof window !== "undefined" && window.location.origin) || "";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/deploy_checklist`, {
        withCredentials: true,
      });
      setSteps(data.steps || []);
      setErr("");
    } catch (e) {
      console.error("Failed to load deploy checklist", e);
      setErr(e?.response?.data?.detail || "Failed to load checklist");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updateStep = async (stepId, patch) => {
    // Optimistic UI
    setSteps((prev) =>
      prev.map((s) => (s.id === stepId ? { ...s, ...patch } : s))
    );
    setSavingId(stepId);
    try {
      await axios.put(`${API}/deploy_checklist/${stepId}`, patch, {
        withCredentials: true,
      });
    } catch (e) {
      console.error("Failed to save deploy step — resyncing", e);
      // Reload on failure to resync
      load();
    } finally {
      setSavingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-leaf-700">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (err) {
    return (
      <div className="flex items-start gap-2 rounded-2xl bg-tomato-100 px-4 py-3 text-sm text-tomato-600">
        <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
        <span>{err}</span>
      </div>
    );
  }

  const doneCount = steps.filter((s) => s.done).length;

  return (
    <div data-testid="admin-deploy" className="space-y-6">
      {/* Hero */}
      <section className="relative overflow-hidden rounded-3xl border-2 border-leaf-200 bg-leaf-50 p-6 sm:p-8">
        <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-sun-300/40 blur-2xl" />
        <div className="relative flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 max-w-xl">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-leaf-600 px-3 py-1 text-xs font-bold uppercase tracking-widest text-white">
              <Rocket className="h-3.5 w-3.5" /> Go live
            </div>
            <h2 className="font-display text-3xl font-bold text-leaf-900 sm:text-4xl">
              Move from preview → laststopstl.com
            </h2>
            <p className="mt-2 text-leaf-900/75">
              Tick each step as you go. Progress saves automatically. The
              recommended path points{" "}
              <code className="rounded bg-white px-1.5 py-0.5 text-leaf-800">
                laststopstl.com
              </code>{" "}
              (apex) and{" "}
              <code className="rounded bg-white px-1.5 py-0.5 text-leaf-800">
                www.laststopstl.com
              </code>{" "}
              at the deployed Emergent app, then 301-redirects the old{" "}
              <code className="rounded bg-white px-1.5 py-0.5 text-leaf-800">
                archcityx.com/laststop
              </code>{" "}
              URL here.
            </p>
          </div>
          <div className="w-full max-w-xs">
            <ProgressBar done={doneCount} total={steps.length} />
          </div>
        </div>
      </section>

      {/* Current preview URL card */}
      <section
        data-testid="deploy-preview-url"
        className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border-2 border-leaf-100 bg-white p-5"
      >
        <div className="min-w-0">
          <p className="text-xs font-black uppercase tracking-widest text-leaf-700">
            Current preview URL
          </p>
          <p className="mt-1 break-all font-mono text-sm text-leaf-900">
            {previewUrl}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <CopyButton text={previewUrl} label="preview-url" />
          <a
            href={previewUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 rounded-full border-2 border-leaf-200 px-3 py-1 text-xs font-bold text-leaf-800 hover:bg-leaf-50"
          >
            Open <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      </section>

      {/* Steps */}
      <ol className="space-y-3">
        {steps.map((step, idx) => (
          <StepItem
            key={step.id}
            step={step}
            idx={idx}
            savingId={savingId}
            onToggle={updateStep}
            onNote={updateStep}
          />
        ))}
      </ol>

      {/* Done banner */}
      {doneCount === steps.length && steps.length > 0 && (
        <div
          data-testid="deploy-done-banner"
          className="rounded-3xl bg-leaf-900 p-6 text-center text-white sm:p-8"
        >
          <Rocket className="mx-auto h-7 w-7 text-sun-300" />
          <h3 className="mt-3 font-display text-2xl font-bold sm:text-3xl">
            You're live. 🎉
          </h3>
          <p className="mt-2 text-leaf-50/85">
            Don't forget to keep an eye on the Bookings tab — fresh requests
            will start rolling in.
          </p>
        </div>
      )}
    </div>
  );
}
